import { decodeEventLog, decodeFunctionResult, encodeFunctionData, type Address, type Hex } from "viem";
import { ponsRewardNftAbi } from "../contracts/pons-reward-nft";
import { contractReadError } from "../contracts/contract-errors";
import { currentChainId, type Eip1193Provider, type WalletTransactionReceipt } from "./eip1193";
import { readRpc, type RpcReadMethod, type RpcReadOptions } from "./read-rpc";
import { ELYSIUM, isElysiumNftPolicy, sameIdentity } from "../config/elysium";

export class FinalizationNotNeeded extends Error {
  constructor() { super("This NFT is already finalized or burned. No transaction was sent; refresh NFT data."); }
}

export async function readNftState(provider: Eip1193Provider,
  input: { chainId: number; collection: Address; tokenId: bigint; block?: { number: string; hash: Hex } },
  assertCurrent: () => void = () => {}, options: RpcReadOptions = {}) {
  const request = async (method: RpcReadMethod, params: readonly unknown[]) => {
    assertCurrent();
    try {
      const result = await readRpc(provider, { method, params }, options);
      assertCurrent();
      return result;
    } catch (error) { options.signal?.throwIfAborted(); assertCurrent(); throw contractReadError(error); }
  };
  assertCurrent();
  if (await currentChainId(provider, options) !== input.chainId) throw new Error(`WRONG_CHAIN:${input.chainId}`);
  assertCurrent();
  if (input.block && (!/^0x[0-9a-f]+$/i.test(input.block.number) || !/^0x[0-9a-f]{64}$/i.test(input.block.hash))) {
    throw new Error("Invalid receipt block identity");
  }
  const raw = await request("eth_call", [{ to: input.collection,
    data: encodeFunctionData({ abi: ponsRewardNftAbi, functionName: "nftState", args: [input.tokenId] }) }, input.block?.number ?? "latest"]);
  if (typeof raw !== "string" || !/^0x(?:[0-9a-f]{2})+$/i.test(raw)) throw new Error("Invalid NFT state response");
  const [sourceToken, tierUsd, rewardRate, bandIndex, status, policyVersion, policyHash] =
    decodeFunctionResult({ abi: ponsRewardNftAbi, functionName: "nftState", data: raw as Hex });
  // The deployed ABI retains the rewardBps field name for V2 PPM rates.
  const v2 = input.chainId === ELYSIUM.chainId && sameIdentity(input.collection, ELYSIUM.collection) &&
    policyVersion === BigInt(ELYSIUM.policyVersion) && sameIdentity(policyHash, ELYSIUM.policyHash);
  if (input.chainId === ELYSIUM.chainId && (!sameIdentity(input.collection, ELYSIUM.collection) ||
    !isElysiumNftPolicy(policyVersion, policyHash))) throw new Error("Invalid NFT reward policy");
  if (policyVersion !== 1n && !v2 || status > 4 || rewardRate > (v2 ? 20_000 : 10_000) || bandIndex > 4) throw new Error("Invalid NFT reward state");
  if (v2 && ![10n, 100n, 500n, 1000n, 2000n, 5000n].includes(tierUsd)) throw new Error("Invalid NFT reward state");
  const rewardDenominator = v2 ? 1_000_000 : 10_000;
  if (input.block) {
    const block = await request("eth_getBlockByNumber", [input.block.number, false]);
    if (!block || typeof block !== "object" || !("hash" in block) ||
      typeof block.hash !== "string" || block.hash.toLowerCase() !== input.block.hash.toLowerCase()) {
      throw new Error("Receipt block is no longer canonical. Check confirmation again.");
    }
  }
  if (await currentChainId(provider, options) !== input.chainId) throw new Error(`WRONG_CHAIN:${input.chainId}`);
  assertCurrent();
  return { sourceToken, tierUsd, rewardBps: v2 ? null : rewardRate, rewardRate, rewardDenominator, bandIndex, status, policyVersion, policyHash };
}

export async function verifyFinalization(provider: Eip1193Provider, receipt: WalletTransactionReceipt,
  input: { chainId: number; collection: Address; tokenId: bigint; sourceToken: string }, assertCurrent: () => void,
  options: RpcReadOptions = {}) {
  const state = await readNftState(provider, { ...input,
    block: { number: receipt.blockNumber, hash: receipt.blockHash } }, assertCurrent, options);
  if (state.sourceToken.toLowerCase() !== input.sourceToken.toLowerCase()) return null;
  let finalized = false;
  let continued = false;
  let exhausted = false;
  for (const log of receipt.logs) {
    if (log.address.toLowerCase() !== input.collection.toLowerCase()) continue;
    try {
      const event = decodeEventLog({ abi: ponsRewardNftAbi, data: log.data, topics: log.topics as [Hex, ...Hex[]] });
      if (event.eventName === "RewardFinalized" && event.args.tokenId === input.tokenId) {
        if (event.args.rewardBps !== state.rewardRate || event.args.bandIndex !== state.bandIndex ||
          event.args.rewardPolicyHash.toLowerCase() !== state.policyHash.toLowerCase()) return null;
        finalized = true;
      }
      if (event.eventName === "RandomnessSamplingContinued" && event.args.tokenId === input.tokenId && event.args.stage <= 1) {
        continued = true;
        exhausted ||= event.args.counterExhausted;
      }
    } catch { /* Unrelated or malformed logs never supply evidence. */ }
  }
  if (state.status === 3 || state.status === 4) return { phase: "CONFIRMED" as const,
    message: finalized ? "Reward finalized. Waiting for canonical metadata and inventory indexing." :
      "Receipt confirmed; this NFT was already finalized or redeemed by another operation. No new reward was chosen." };
  if (state.status === 2 && continued && !finalized) return {
    phase: exhausted ? "SAMPLING_EXHAUSTED" as const : "SAMPLING_REQUIRED" as const,
    message: exhausted ? "Transaction confirmed, but the sampling counter is exhausted. Operator review is required; do not retry." :
      "Transaction confirmed. Sampling is unfinished; refresh NFT state before explicitly continuing finalization. No reward is finalized yet.",
  };
  return null;
}

/** Elysium mints must finalize in this same receipt, not in a later transaction. */
export async function verifyAtomicMint(provider: Eip1193Provider, receipt: WalletTransactionReceipt,
  input: { chainId: number; collection: Address; tokenId: bigint; sourceToken: string; wallet: string },
  assertCurrent: () => void, options: RpcReadOptions = {}): Promise<boolean> {
  if (receipt.status !== "0x1") return false;
  const state = await readNftState(provider, { ...input, block: { number: receipt.blockNumber, hash: receipt.blockHash } }, assertCurrent, options);
  if (state.status !== 3 || state.sourceToken.toLowerCase() !== input.sourceToken.toLowerCase()) return false;
  let minted = false, finalized = false;
  for (const log of receipt.logs) {
    if (log.address.toLowerCase() !== input.collection.toLowerCase()) continue;
    try {
      const event = decodeEventLog({ abi: ponsRewardNftAbi, data: log.data, topics: log.topics as [Hex, ...Hex[]] });
      if (event.eventName === "Transfer" && event.args.tokenId === input.tokenId &&
        BigInt(event.args.from) === 0n && event.args.to.toLowerCase() === input.wallet.toLowerCase()) minted = true;
      if (event.eventName === "RewardFinalized" && event.args.tokenId === input.tokenId &&
        event.args.rewardBps === state.rewardRate && event.args.bandIndex === state.bandIndex &&
        event.args.rewardPolicyHash.toLowerCase() === state.policyHash.toLowerCase()) finalized = true;
    } catch { /* Unrelated/malformed logs grant nothing. */ }
  }
  return minted && finalized;
}
